(() => {
  // Project rotation into the photographed propeller plane, keeping its shaft fixed.
  const anchoredRotation = (angle, cx, cy, aspect, planeAngle) => {
    const theta = angle*Math.PI/180, phi = planeAngle*Math.PI/180;
    const c = Math.cos(phi), s = Math.sin(phi);
    const b00 = aspect*c*c+s*s, b01 = (aspect-1)*c*s, b11 = aspect*s*s+c*c;
    const i00 = b11/aspect, i01 = -b01/aspect, i11 = b00/aspect;
    const ct = Math.cos(theta), st = Math.sin(theta);
    const m00 = (b00*ct+b01*st)*i00+(-b00*st+b01*ct)*i01;
    const m01 = (b00*ct+b01*st)*i01+(-b00*st+b01*ct)*i11;
    const m10 = (b01*ct+b11*st)*i00+(-b01*st+b11*ct)*i01;
    const m11 = (b01*ct+b11*st)*i01+(-b01*st+b11*ct)*i11;
    return `matrix(${m00} ${m10} ${m01} ${m11} ${cx-m00*cx-m01*cy} ${cy-m10*cx-m11*cy})`;
  };
  const init = () => document.querySelectorAll('[data-flight-detail]').forEach(root => {
    if (root.dataset.fsReady) return;
    root.dataset.fsReady = 'true';
    const reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;
    const animate = async (panel, {replay = false, force = false} = {}) => {
      if (replay) {
        cancelAnimationFrame(panel.replayFrame);
        delete panel.dataset.fsPlayed; delete panel.dataset.fsLoading; delete panel.dataset.fsRunning;
        panel.classList.remove('fs-animating');
      }
      if (panel.dataset.fsPlayed || panel.dataset.fsLoading || panel.dataset.fsRunning) return;
      const run = (panel.animationRun || 0) + 1;
      panel.animationRun = run;
      panel.dataset.fsLoading = 'true';
      const counters = [...panel.querySelectorAll('[data-fs-count]')];
      const specBars = panel.hasAttribute('data-fs-spec-panel') ? [...panel.querySelectorAll('.fs-bar i')] : [];
      const format = value => Math.round(value).toLocaleString(document.documentElement.lang || 'en');
      if (reduced && !force) {panel.dataset.fsPlayed='true';counters.forEach(el => el.textContent = format(Number(el.dataset.fsCount))); return;}
      const art = panel.querySelector('[data-fs-art]');
      const ready = await Promise.all([...new Set([...art.querySelectorAll('image')].map(image=>image.getAttribute('href')))].map(src=>new Promise(resolve=>{
        const image = new Image();image.onload=()=>resolve(true);image.onerror=()=>resolve(false);image.src=src;
      })));
      if (panel.animationRun !== run) return;
      delete panel.dataset.fsLoading;
      if (panel.hidden || ready.includes(false)) return;
      panel.dataset.fsPlayed = 'true';
      panel.dataset.fsRunning = 'true';
      panel.classList.add('fs-animating');
      specBars.forEach(bar => {bar.style.transform = 'scaleX(0)';});
      const rotor = art?.querySelector('[data-fs-rotor]');
      const motionBlur = art.querySelector('[data-fs-motion-blur]');
      const hub = art?.querySelector('[data-fs-hub]');
      const motor = art.querySelector('[data-fs-motor-face]');
      const motorBlur = art.querySelector('[data-fs-motor-blur]');
      const cx = Number(art?.dataset.cx), cy = Number(art?.dataset.cy);
      const aspect = Number(art?.dataset.perspective), planeAngle = Number(art?.dataset.planeAngle);
      const motorCx = Number(art.dataset.motorCx), motorCy = Number(art.dataset.motorCy);
      const motorAspect = Number(art.dataset.motorRx)/Number(art.dataset.motorRy);
      const motorAngle = Number(art.dataset.motorAngle);
      const start = performance.now();
      const frame = time => {
        if (panel.animationRun !== run) return;
        const progress = Math.min(1,(time-start)/2500);
        // Use the propeller's frame clock so the bars complete exactly as it stops.
        specBars.forEach(bar => {bar.style.transform = `scaleX(${progress})`;});
        const countProgress = Math.min(1,(time-start)/1600);
        counters.forEach(el => el.textContent = format(Number(el.dataset.fsCount)*(1-Math.pow(1-countProgress,3))));
        if (rotor) {
          const rampEnd = .4, coastEnd = .72, braking = 1-coastEnd;
          let speed, travel;
          if (progress < rampEnd) {
            speed = (progress/rampEnd)**2;
            travel = progress**3/(3*rampEnd**2);
          } else if (progress < coastEnd) {
            speed = 1; travel = rampEnd/3+progress-rampEnd;
          } else {
            speed = ((1-progress)/braking)**2;
            travel = rampEnd/3+coastEnd-rampEnd+braking/3*(1-((1-progress)/braking)**3);
          }
          const phase = travel/(rampEnd/3+coastEnd-rampEnd+braking/3);
          const blend = Math.max(0,Math.min(1,(speed-.18)/.72));
          const strength = blend*blend*(3-2*blend);
          const rotation = anchoredRotation(21600*phase,cx,cy,aspect,planeAngle);
          rotor.setAttribute('transform',rotation);
          rotor.style.opacity = String(1-strength);
          rotor.style.filter = `blur(${strength*2}px)`;
          motionBlur.setAttribute('opacity',String(strength));
          // The real shaft cap and blades share a transform; housing stays fixed.
          hub.setAttribute('transform',rotation);
          hub.setAttribute('opacity',String(1-strength));
          motor.setAttribute('transform',anchoredRotation(21600*phase,motorCx,motorCy,motorAspect,motorAngle));
          motorBlur?.setAttribute('transform',anchoredRotation(21600*phase,motorCx,motorCy,motorAspect,motorAngle));
          motor.setAttribute('opacity',String(1-strength));
        }
        if (progress < 1) panel.replayFrame = requestAnimationFrame(frame);
        else {
          // Finish on the same composite. Swapping photographs caused a visible jump.
          rotor.removeAttribute('transform');rotor.style.opacity='1';rotor.style.filter='none';
          hub.removeAttribute('transform');motionBlur.setAttribute('opacity','0');
          hub.setAttribute('opacity','1');
          motor.removeAttribute('transform');motor.setAttribute('opacity','1');
          motorBlur?.removeAttribute('transform');
          delete panel.dataset.fsRunning;
        }
      };
      panel.replayFrame = requestAnimationFrame(frame);
    };
    root.querySelectorAll('[data-fs-art]').forEach(art => {
      const panel = art.closest('[role="tabpanel"], [data-fs-spec-panel]');
      if (panel) window.TytoAnimationReplay?.mount(art.closest('.fs-model__image'), () => animate(panel, {replay: true, force: true}));
    });
    const tabs = [...root.querySelectorAll('[role="tab"]')];
    const select = tab => {
      tabs.forEach(item => {
        const active = item === tab;
        item.setAttribute('aria-selected',String(active)); item.tabIndex = active?0:-1;
        const panel = document.getElementById(item.getAttribute('aria-controls'));
        panel.hidden = !active;
        if (active) animate(panel);
      });
      const activePanel = document.getElementById(tab.getAttribute('aria-controls'));
      root.querySelectorAll('[data-fs-previous], [data-fs-next]').forEach(button => button.setAttribute('aria-controls', activePanel.id));
      const index = tabs.indexOf(tab);
      root.querySelectorAll('[data-fs-previous]').forEach(button => { button.disabled = index === 0; });
      root.querySelectorAll('[data-fs-next]').forEach(button => { button.disabled = index === tabs.length - 1; });
    };
    tabs.forEach((tab,index) => {
      tab.addEventListener('click',()=>select(tab));
      tab.addEventListener('keydown',event=> {
        let next;
        if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') next = (index + window.TytoDirection.arrow(event.key, root) + tabs.length) % tabs.length;
        if(event.key==='Home') next=0;
        if(event.key==='End') next=tabs.length-1;
        if(next===undefined)return;
        event.preventDefault();select(tabs[next]);tabs[next].focus();
      });
    });
    const moveModel = direction => {
      if (!tabs.length) return;
      const current = tabs.findIndex(tab => tab.getAttribute('aria-selected') === 'true');
      select(tabs[Math.max(0, Math.min(tabs.length - 1, current + direction))]);
    };
    root.querySelector('[data-fs-previous]')?.addEventListener('click', () => moveModel(-1));
    root.querySelector('[data-fs-next]')?.addEventListener('click', () => moveModel(1));
    root.querySelectorAll('[data-fs-previous]').forEach(button => { button.disabled = true; });
    root.querySelectorAll('[data-fs-next]').forEach(button => { button.disabled = tabs.length <= 1; });
    const panel = root.querySelector('[role="tabpanel"], [data-fs-spec-panel]');
    if (panel) root.querySelectorAll('[data-fs-previous], [data-fs-next]').forEach(button => button.setAttribute('aria-controls', panel.id));
    if(panel && 'IntersectionObserver' in window) {
      const repeatSpecs = panel.hasAttribute('data-fs-spec-panel');
      let entered = false;
      const observer = new IntersectionObserver(entries=>entries.forEach(entry=>{
        if (repeatSpecs) {
          if (!entry.isIntersecting) entered = false;
          if (entry.intersectionRatio >= .4 && !entered && !panel.dataset.fsRunning) {
            entered = true;
            delete panel.dataset.fsPlayed;
            panel.classList.remove('fs-animating');
            // Reset the bar animation before replaying it with the propeller and counters.
            void panel.offsetWidth;
            animate(panel);
          }
        } else if(entry.isIntersecting) {animate(panel);observer.disconnect();}
      }),{threshold:repeatSpecs ? [0,.4] : .25});
      observer.observe(panel);
    } else if(panel) animate(panel);
  });
  if(document.readyState==='loading')document.addEventListener('DOMContentLoaded',init,{once:true});else init();
  document.addEventListener('shopify:section:load',init);
})();
